// src/components/onboarding-tooltip.tsx
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, X, Building, Users, UserCheck, Shield, Sparkles, AlertCircle } from 'lucide-react';

interface OnboardingTooltipProps {
  step: any;
  stepNumber: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  position: { top: number; left: number };
  elementFound?: boolean;
}

export const OnboardingTooltip = ({ 
  step, 
  stepNumber, 
  totalSteps, 
  onNext, 
  onSkip, 
  position,
  elementFound = true
}: OnboardingTooltipProps) => {
  
  // Icon mapping
  const iconMap = {
    'Sparkles': <Sparkles className="h-5 w-5" />,
    'Building': <Building className="h-5 w-5" />,
    'Shield': <Shield className="h-5 w-5" />,
    'Users': <Users className="h-5 w-5" />,
    'UserCheck': <UserCheck className="h-5 w-5" />
  };

  const tooltipStyle = {
    top: `${position.top}px`,
    left: `${position.left}px`,
    transform: 'translateY(-50%)',
    maxWidth: '90vw',
    maxHeight: '90vh'
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[100]" />
      
      {/* Tooltip */}
      <div 
        className="fixed z-[101] w-80"
        style={tooltipStyle}
      >
        <Card className="shadow-2xl border-2 border-blue-200 bg-white dark:bg-gray-900">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  Step {stepNumber} of {totalSteps}
                </Badge>
                {!elementFound && (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-200">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Element not found
                  </Badge>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={onSkip} className="h-6 w-6 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex items-start gap-3 mb-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg text-blue-600 dark:text-blue-400 flex-shrink-0">
                {iconMap[step.icon as keyof typeof iconMap] || <Sparkles className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{step.title}</h3>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{step.description}</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">{step.content}</p>
            
            {!elementFound && (
              <div className="mb-4 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-700 dark:text-orange-300">
                The target element for this step wasn't found. You can still continue with the tutorial.
              </div>
            )}
            
            <div className="flex justify-between">
              <Button variant="ghost" size="sm" onClick={onSkip} className="text-gray-600 hover:text-gray-800">
                Skip Tour
              </Button>
              <Button size="sm" onClick={onNext} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                {stepNumber === totalSteps ? 'Complete' : step.actionText || 'Continue'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Arrow pointing to the element (only if element found) */}
        {elementFound && (
          <div className="absolute top-1/2 -left-2 transform -translate-y-1/2">
            <div className="w-0 h-0 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-blue-200"></div>
            <div className="absolute top-1/2 -left-1 transform -translate-y-1/2">
              <div className="w-0 h-0 border-t-6 border-b-6 border-r-6 border-t-transparent border-b-transparent border-r-white dark:border-r-gray-900"></div>
            </div>
          </div>
        )}
      </div>
      
      {/* Enhanced highlight styles */}
      {/* @ts-ignore */}
      <style jsx global>{`
        .onboarding-highlight {
          position: relative !important;
          z-index: 102 !important;
          border-radius: 8px !important;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.4) !important;
          transition: all 0.3s ease !important;
          background-color: rgba(59, 130, 246, 0.05) !important;
          animation: onboarding-pulse 2s infinite;
        }
        
        @keyframes onboarding-pulse {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.6), 0 0 30px rgba(59, 130, 246, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.4), 0 0 40px rgba(59, 130, 246, 0.6);
          }
        }
        
        .onboarding-highlight::before {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border: 2px solid rgba(59, 130, 246, 0.8);
          border-radius: 8px;
          pointer-events: none;
          z-index: -1;
        }
      `}</style>
    </>
  );
};